namespace SolovisGateway.Admin.Users {

    /**
     * Confirmation dialog for sending a user a password-reset email from the Solovis Auth0 tenant.
     *
     * Reusable across admin surfaces (User Admin, Org Admin, ...). Show it via
     * UserSendPasswordResetDialog.showDialog({ userId, contactEmail?, primaryEmail?, endpointUrl?, saveCallback? }):
     *   - userId:       the Gateway user to email (required).
     *   - contactEmail: the address the reset email is sent to; falls back to "this user" if absent.
     *   - primaryEmail: optional. Pass this ONLY when it should be displayed alongside contactEmail
     *                   (i.e. the caller has already determined it's a well-formed, distinct address —
     *                   PrimaryEmail is the Auth0 username and is not always a real mailbox for legacy
     *                   common-auth accounts, so this dialog does no email-format validation itself).
     *                   When provided, both addresses are shown in bold, each labeled with which one
     *                   it is, since the reset email is cc'd to it.
     *   - endpointUrl:  optional RPC endpoint to invoke; defaults to the Gateway Admin route.
     *                   Org Admin passes "~/Organization/SendPasswordResetEmail".
     *   - saveCallback: optional, invoked after the email is sent (e.g. to show a success toast).
     *
     * The send runs via invokeAndWait, so the loading overlay shows while it works and the dialog
     * stays open behind it. On success the dialog closes and saveCallback fires; on failure the error
     * is shown in-dialog and the dialog stays open so the admin can retry.
     */
    export class UserSendPasswordResetDialog {

        private _userId;
        private _contactEmail;
        private _primaryEmail;
        private _endpointUrl;
        private _saveCallback;
        private _dialogWindow;
        private _messageDiv;

        constructor(options) {
            var self = this;

            this._userId = options.userId ? options.userId : null;
            this._contactEmail = options.contactEmail ? options.contactEmail : null;
            this._primaryEmail = options.primaryEmail ? options.primaryEmail : null;
            this._endpointUrl = options.endpointUrl ? options.endpointUrl : "~/Admin/UserAdmin/SendPasswordResetEmail";
            this._saveCallback = options.saveCallback ? options.saveCallback : null;

            var desiredWidth = Math.min($(window).width() * .8, 480);
            var desiredHeight = Math.min($(window).height() * .8, 220);

            var bodyDiv = $("<div>")
                .css("min-height", "60px");

            var messageParagraph = $("<div>").appendTo(bodyDiv);
            messageParagraph.append("Send a Log-In password reset email to ");

            if (this._contactEmail && this._primaryEmail) {
                messageParagraph.append($("<b>").text(this._contactEmail));
                messageParagraph.append(" (Contact Email) and ");
                messageParagraph.append($("<b>").text(this._primaryEmail));
                messageParagraph.append(" (Primary Email)");
            }
            else {
                messageParagraph.append($("<b>").text(this._contactEmail ? this._contactEmail : "this user"));
            }

            messageParagraph.append("? They will receive a link to set a new password.");

            this._messageDiv = $("<div>")
                .css({ "display": "none", "margin-top": "10px", "color": "#c62828" })
                .appendTo(bodyDiv);

            this._dialogWindow = new Solovis.UI.Dialog({
                container: bodyDiv,

                title: "Send Log-In Password Reset Email",

                modal: true,
                center: true,
                actions: ["Close"],
                autoOpen: true,
                destroyOnClose: true,
                width: desiredWidth,
                minWidth: 100,
                minHeight: 100,
                height: desiredHeight,
                buttons: [
                    {
                        label: "Send Log-In Password Reset Email",
                        click: function () {
                            self._send();
                        }
                    },
                    {
                        label: "Cancel",
                        click: function () {
                            self._dialogWindow.close();
                        }
                    }
                ]
            });
        }

        public _send() {
            var self = this;

            self._messageDiv.hide();

            Solovis.Rpc.invokeAndWait(this._endpointUrl,
                {
                    userId: this._userId
                },
                function (response, success) {
                    var modelState = response ? response.result : null;
                    if (success && modelState && modelState.modelIsValid !== false) {
                        self._dialogWindow.close();
                        if (self._saveCallback) {
                            self._saveCallback();
                        }
                    }
                    else {
                        self._showError(self._extractErrorMessage(response));
                    }
                });
        }

        private _extractErrorMessage(response) {
            var modelState = response ? response.result : null;
            if (modelState && modelState.errors && modelState.errors.length) {
                return modelState.errors[0].message ? modelState.errors[0].message : "Failed to send the password reset email. Please try again.";
            }
            if (response && response.error && response.error.message) {
                return response.error.message;
            }
            return "Failed to send the password reset email. Please try again.";
        }

        private _showError(message) {
            this._messageDiv
                .css("display", "block")
                .empty()
                .append($("<span>").addClass("fa fa-exclamation-circle").css("margin-right", "6px"))
                .append($("<span>").text(message));
        }

        public static showDialog(options) {
            return new SolovisGateway.Admin.Users.UserSendPasswordResetDialog(options);
        }
    }
}
